export type UUID = string;
export type Path = string;
export type DateTime = string;
export type ShiftStatus = 'PreSubmission' | 'Submitted' | 'TakenOver' | 'InProgress'; // Not sure if these are correct or exhaustive. Already appears to be missing the "InProgress" status which is used to render the tags
export type FileStatus = 'Download' | 'NoDownload' | 'NoFile'; // Not sure if these are correct or exhaustive
export type AssetName = string;
export type HotoRowKey = string;
export type SnippetRowKey = string;
export type RowData = string | number | boolean | null | undefined;
export type ServerBool = boolean | 'True' | 'False' | 'true' | 'false' | null;
export enum AssetType {
  Site = 'Site',
  BusinessTeam = 'Business Team',
  Console = 'Console',
  Post = 'Post',
}
export type ControlType =
  | 'Text'
  | 'Checkbox'
  | 'DateTime'
  | 'Date'
  | 'Time'
  | 'Dropdown'
  | 'Static Text'
  | 'Text - Single Line'
  | 'Numeric';

export type SnippetType =
  | 'Tag'
  | 'Task'
  | 'Table'
  | 'Matrix'
  | 'Comment'
  | 'Monitoring'
  | 'Instruction'
  | 'RollupMatrix'
  | 'StandingOrder'
  | 'RollupComment'
  | 'DataEntryTable'
  | 'RollupDataEntryTable'
  | 'snippetGroup';

export type StandingOrderAction = 'Activate' | 'Complete' | 'Comment';

export type StandingOrderStatus = 'Approved' | 'Planned' | 'Activated' | 'Completed';

export type TaskAction = 'Start' | 'Pause' | 'Resume' | 'Complete' | 'Cancel';

export type TaskStatus =
  | 'In-Progress'
  | 'Paused'
  | 'Started'
  | 'Planned'
  | 'Completed'
  | 'Cancelled';

export type SnippetDataDictionary = Record<SnippetRowKey, RowData>;

export interface User {
  name: string;
  email: string;
}

export interface Heartbeat {
  Service: string;
  Timestamp: Date;
}

export interface APIHierarchy {
  lastUpdate: DateTime;
  isUpdate: boolean;
  sites: APISite[];
}

export interface Asset {
  assetName: AssetName;
  displayName: string;
  path: string;
}

export interface APISite extends Asset {
  siteCode: string;
  businessTeams: APIBusinessTeam[];
}

export interface APIBusinessTeam extends Asset {
  consoles: APIConsole[];
}

export interface APIConsole extends Asset {
  posts: Post[];
}

export interface Hierarchy {
  sites: { [name: string]: Site };
}

export interface Site extends Asset {
  siteCode: string;
  businessTeams: { [name: string]: BusinessTeam };
}

export interface BusinessTeam extends Asset {
  consoles: { [name: string]: Console };
}

export interface Console extends Asset {
  posts: { [name: string]: Post };
}

export type Post = Asset;

export type AssetDictionary =
  | {
      [name: string]: Site;
    }
  | {
      [name: string]: BusinessTeam;
    }
  | {
      [name: string]: Console;
    }
  | {
      [name: string]: Post;
    };

export interface ShiftLog {
  shiftSummaryID: UUID;
  templateName: string;
  assetName: string;
  path: Path;
  createUser: string;
  createTime: DateTime;
  submitUser: string;
  submitTime: DateTime;
  takeoverUser: string;
  takeoverTime: DateTime;
  approveUser: string;
  approveTime: DateTime;
  shiftStart: DateTime;
  shiftEnd: DateTime;
  status: ShiftStatus;
  takeoverDisplay: boolean;
  filename: string | null;
  fileStatus: FileStatus;
  fileContent: string | null;
}

export interface ShiftLogDetails {
  shiftSummaryID: UUID;
  snippets: Snippet[];
}

export interface MatrixCell {
  rowNumber: number;
  colNumber: number;
  controlType: ControlType;
  controlValues: string[] | null;
}

export interface SnippetConfiguration {
  columnCount: number;
  columns: SnippetConfigurationColumn[];
  rowTitleKey: string;
  matrixCells: MatrixCell[] | null;
}

export interface SnippetConfigurationColumn {
  id: number;
  name: SnippetRowKey;
  displayName: string;
  controlType: ControlType;
  controlValues: string[] | null;
  visible: ServerBool;
}

export interface RollupDetails {
  SourceSnippetDisplayID: UUID;
  Asset?: string;
  Author?: string;
  CreatedTime?: DateTime;
}

export interface SnippetData {
  rollupDetails: RollupDetails | null;
  rowCount: number;
  rowData: SnippetDataDictionary[];
}

export interface Snippet {
  shiftsummaryDisplyId: UUID;
  displayTemplateId: UUID;
  count: number;
  configuration: SnippetConfiguration | null;
  data: SnippetData[];
  description: string;
  displayName: string;
  enableComment: boolean | null;
  enableEdit: ServerBool;
  groupSnippets: Snippet[] | null;
  isEdited: boolean | null;
  name: string;
  order: number;
  type: SnippetType;
  maxRows: number;
  canAddDeleteRows: ServerBool;
}

export interface CommentSnippet extends Snippet {
  type: Extract<SnippetType, 'Comment'>;
}

export interface TabularDataSnippet extends Snippet {
  type: Extract<SnippetType, 'DataEntryTable' | 'Matrix' | 'Table'>;
}

export interface UserPreferences {
  assetName: string;
  path: Path;
  preferences: APIPreferences;
  previouslySet: boolean;
  shiftName: string;
  currentShiftStart: DateTime;
  currentShiftEnd: DateTime;
  previousShiftStart: DateTime;
  previousShiftEnd: DateTime;
}

export interface APIPreferences {
  site?: string;
  businessTeam?: string;
  console?: string;
  post?: string;
}

export interface Preferences {
  site?: Site;
  businessTeam?: BusinessTeam;
  console?: Console;
  post?: Post;
  selectedAsset?: Asset;
  path?: string;
}

export interface ReportTemplate {
  templateName: UUID;
  name: string;
  displayName: string;
}
export interface TakeOverDetails {
  name: string;
  configuration: HotoConfiguration[];
  data: HotoData;
}

interface HotoConfiguration {
  count: number;
  columns: HotoColumnConfiguration[];
}

interface HotoColumnConfiguration {
  name: HotoRowKey;
  control: string;
}

interface HotoData {
  count: number;
  rowData: Record<HotoRowKey, RowData>[];
}

export interface ShiftLogStatusChangeResponse {
  status: boolean;
  message: string;
}

export interface CreateShiftLogResponse extends ShiftLog {
  snippets: Snippet[];
}

export interface UpdateShiftLogResponse {
  displayId: UUID;
  status: boolean;
  message: string;
}

export interface StandingOrderActionResponse {
  standingOrderId: UUID;
  statusDisplayName: StandingOrderStatus;
}

export interface TaskActionResponse {
  taskId: UUID;
  name: string;
  assetDisplayName: string;
  typeDisplayName: string;
  priority: string;
  startTime: string;
  endTime: string;
  assignees: string;
  state: TaskStatus;
  actualStartTime: string;
  actualEndTime: string;
  completionPercent: string;
  description: string;
}
